import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import styles from './ui.module.css';

/**
 * Reusable accessible primitives. Semantic HTML first; ARIA is added only
 * where native semantics fall short (spec section 12).
 */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: 'normal' | 'small';
};

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: styles.primary ?? '',
  secondary: '',
  ghost: styles.ghost ?? '',
  danger: styles.danger ?? '',
};

export function Button({
  variant = 'secondary',
  size = 'normal',
  className,
  type = 'button',
  ...rest
}: ButtonProps): ReactNode {
  const classes = [
    styles.button,
    VARIANT_CLASS[variant],
    size === 'small' ? styles.small : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return <button type={type} className={classes} {...rest} />;
}

export function Panel({
  title,
  actions,
  children,
  as: Tag = 'section',
  labelledBy,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  as?: 'section' | 'div' | 'article';
  labelledBy?: string;
}): ReactNode {
  const headingId = useId();

  return (
    <Tag
      className={styles.panel}
      aria-labelledby={title ? headingId : labelledBy}
    >
      {(title ?? actions) ? (
        <div className={styles.panelHeader}>
          {title ? (
            <h3 id={headingId} className={styles.sectionTitle}>
              {title}
            </h3>
          ) : (
            <span />
          )}
          {actions}
        </div>
      ) : null}
      {children}
    </Tag>
  );
}

type FieldShellProps = {
  label: string;
  required?: boolean | undefined;
  description?: string | undefined;
  error?: string | undefined;
  children: (ids: {
    controlId: string;
    describedBy?: string | undefined;
  }) => ReactNode;
};

/**
 * Wires label, description, and error text to the control via `aria-describedby`
 * so screen readers announce the "why this matters" copy and any validation
 * message together with the field.
 */
export function FieldShell({
  label,
  required,
  description,
  error,
  children,
}: FieldShellProps): ReactNode {
  const controlId = useId();
  const descriptionId = `${controlId}-description`;
  const errorId = `${controlId}-error`;

  const describedBy =
    [description ? descriptionId : null, error ? errorId : null]
      .filter(Boolean)
      .join(' ') || undefined;


  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={controlId}>
        {label}
        {required ? (
          <abbr className={styles.required} title="required">
            *
          </abbr>
        ) : null}
      </label>

      {children({ controlId, describedBy })}

      {description ? (
        <p id={descriptionId} className={styles.description}>
          {description}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} className={styles.error} role="alert">
          <span className={styles.errorMark} aria-hidden="true">
            !
          </span>
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}

export type TextFieldProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'id' | 'aria-describedby'
> & {
  label: string;
  description?: string | undefined;
  error?: string | undefined;
};

export function TextField({
  label,
  description,
  error,
  required,
  ...rest
}: TextFieldProps): ReactNode {
  return (
    <FieldShell
      label={label}
      required={required}
      description={description}
      error={error}
    >
      {({ controlId, describedBy }) => (
        <input
          id={controlId}
          className={[styles.control, error ? styles.invalid : '']
            .filter(Boolean)
            .join(' ')}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          required={required}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

export type NumberFieldProps = TextFieldProps & {
  /** Unit shown beside the control, e.g. "g" or "kWh". */
  suffix?: string | undefined;
};

export function NumberField({
  label,
  description,
  error,
  suffix,
  required,
  ...rest
}: NumberFieldProps): ReactNode {
  return (
    <FieldShell
      label={label}
      required={required}
      description={description}
      error={error}
    >
      {({ controlId, describedBy }) => (
        <span className={styles.suffix}>
          <input
            id={controlId}
            type="number"
            inputMode="decimal"
            className={[
              styles.control,
              styles.numeric,
              error ? styles.invalid : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            required={required}
            {...rest}
          />
          {suffix ? (
            <span className={styles.suffixText} aria-hidden="true">
              {suffix}
            </span>
          ) : null}
        </span>
      )}
    </FieldShell>
  );
}

export type SelectFieldProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'id' | 'aria-describedby'
> & {
  label: string;
  description?: string | undefined;
  error?: string | undefined;
  options: readonly {
    value: string;
    label: string;
    disabled?: boolean | undefined;
  }[];
};

export function SelectField({
  label,
  description,
  error,
  options,
  required,
  ...rest
}: SelectFieldProps): ReactNode {
  return (
    <FieldShell
      label={label}
      required={required}
      description={description}
      error={error}
    >
      {({ controlId, describedBy }) => (
        <select
          id={controlId}
          className={[styles.control, error ? styles.invalid : '']
            .filter(Boolean)
            .join(' ')}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          required={required}
          {...rest}
        >
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  );
}

export type ChoiceOption = {
  value: string;
  label: string;
};

/**
 * A small set of known answers offered as pills, with an optional escape
 * hatch for the one grower whose medium is not on the list. Built on real
 * radio inputs so arrow-key navigation and grouping come from the platform.
 *
 * Use this instead of `SelectField` when the options are few, short, and
 * worth reading at a glance — picking "Coco" should not cost a menu.
 */
export function ChoiceField({
  label,
  description,
  error,
  required,
  options,
  value,
  onChange,
  custom,
}: {
  label: string;
  description?: string | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
  options: readonly ChoiceOption[];
  value: string;
  onChange: (next: string) => void;
  /** Adds a final pill that reveals a free-text field. */
  custom?: { label: string; placeholder?: string } | undefined;
}): ReactNode {
  const groupId = useId();
  const descriptionId = `${groupId}-description`;
  const errorId = `${groupId}-error`;

  // A value that matches no pill can only have come from the custom field, so
  // an edited record reopens on the same control the user typed it into.
  const [customOpen, setCustomOpen] = useState(
    () => value !== '' && !options.some((option) => option.value === value),
  );

  const describedBy =
    [description ? descriptionId : null, error ? errorId : null]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <fieldset
      className={styles.choiceField}
      aria-describedby={describedBy}
      aria-invalid={error ? true : undefined}
    >
      <legend className={styles.label}>
        {label}
        {required ? (
          <abbr className={styles.required} title="required">
            *
          </abbr>
        ) : null}
      </legend>

      <div className={styles.choices}>
        {options.map((option) => (
          <label key={option.value} className={styles.choice}>
            <input
              type="radio"
              name={groupId}
              className={styles.choiceInput}
              value={option.value}
              checked={!customOpen && value === option.value}
              onChange={() => {
                setCustomOpen(false);
                onChange(option.value);
              }}
            />
            <span className={styles.choiceFace}>{option.label}</span>
          </label>
        ))}

        {custom ? (
          <label className={styles.choice}>
            <input
              type="radio"
              name={groupId}
              className={styles.choiceInput}
              value="__custom__"
              checked={customOpen}
              onChange={() => {
                setCustomOpen(true);
                onChange('');
              }}
            />
            <span className={styles.choiceFace}>{custom.label}</span>
          </label>
        ) : null}
      </div>

      {customOpen && custom ? (
        <input
          type="text"
          className={[styles.control, error ? styles.invalid : '']
            .filter(Boolean)
            .join(' ')}
          // Not `custom.label`: that already names the pill that reveals this
          // field, and two controls in one group answering to the same name
          // is ambiguous for anyone navigating by name.
          aria-label={`Custom ${label.toLowerCase()}`}
          placeholder={custom.placeholder}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      ) : null}

      {description ? (
        <p id={descriptionId} className={styles.description}>
          {description}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} className={styles.error} role="alert">
          <span className={styles.errorMark} aria-hidden="true">
            !
          </span>
          <span>{error}</span>
        </p>
      ) : null}
    </fieldset>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}): ReactNode {
  return (
    <div className={styles.empty}>
      <p className={styles.emptyTitle}>{title}</p>
      <p className={styles.emptyBody}>{body}</p>
      {action}
    </div>
  );
}

export function Notice({
  tone = 'info',
  children,
  role,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'success';
  children: ReactNode;
  role?: 'status' | 'alert';
}): ReactNode {
  const toneClass =
    tone === 'warning'
      ? styles.noticeWarning
      : tone === 'danger'
        ? styles.noticeDanger
        : tone === 'success'
          ? styles.noticeSuccess
          : styles.noticeInfo;

  return (
    <p className={[styles.notice, toneClass].join(' ')} role={role}>
      {children}
    </p>
  );
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?:
    | 'neutral'
    | 'baseline'
    | 'emerging'
    | 'developing'
    | 'calibrated'
    | 'warning';
}): ReactNode {
  const toneClass = {
    neutral: '',
    baseline: styles.badgeBaseline,
    emerging: styles.badgeEmerging,
    developing: styles.badgeDeveloping,
    calibrated: styles.badgeCalibrated,
    warning: styles.badgeWarning,
  }[tone];

  return (
    <span className={[styles.badge, toneClass].filter(Boolean).join(' ')}>
      {children}
    </span>
  );
}

/**
 * A modal built on the native `<dialog>` element, so focus trapping, Escape
 * handling, and the top layer come from the platform rather than hand-rolled
 * JavaScript. Focus is restored to the invoking control on close.
 */
export function Dialog({
  open,
  title,
  onClose,
  children,
  actions,
  size = 'normal',
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
  /** "wide" makes room for a second column, such as a live preview. */
  size?: 'normal' | 'wide';
}): ReactNode {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    if (!open) {
      if (element.open) element.close();
      return;
    }

    const returnFocus =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    if (!element.open) element.showModal();

    return () => {
      if (element.open) element.close();
      // Wizards unmount as soon as their controlled `open` state changes, so
      // cleanup must restore the trigger as well as an ordinary prop change.
      // Source: https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/
      returnFocus?.focus();
    };
  }, [open]);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const handleCancel = (event: Event): void => {
      event.preventDefault();
      onClose();
    };

    element.addEventListener('cancel', handleCancel);
    return () => {
      element.removeEventListener('cancel', handleCancel);
    };
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      className={[styles.dialog, size === 'wide' ? styles.dialogWide : '']
        .filter(Boolean)
        .join(' ')}
      aria-labelledby={titleId}
    >
      <div className={styles.dialogInner}>
        <h2 id={titleId} className={styles.dialogTitle}>
          {title}
        </h2>
        {children}
        <div className={styles.dialogActions}>{actions}</div>
      </div>
    </dialog>
  );
}

/** A thumbnail with an explicit, keyboard-accessible full-image preview. */
export function ExpandableImage({
  src,
  alt,
  className,
  loading = 'lazy',
}: {
  src: string;
  alt: string;
  className?: string | undefined;
  loading?: 'eager' | 'lazy';
}): ReactNode {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className={styles.imageTrigger}
        aria-label={`Open ${alt} full size`}
        aria-haspopup="dialog"
        onClick={() => {
          setOpen(true);
        }}
      >
        <img
          className={className}
          src={src}
          alt={alt}
          loading={loading}
          decoding="async"
        />
        <span className={styles.imageHint} aria-hidden="true">
          View
        </span>
      </button>

      <Dialog
        open={open}
        title="Image preview"
        onClose={() => {
          setOpen(false);
        }}
        actions={
          <Button
            onClick={() => {
              setOpen(false);
            }}
          >
            Close
          </Button>
        }
      >
        {open ? (
          <img className={styles.expandedImage} src={src} alt={alt} />
        ) : null}
      </Dialog>
    </>
  );
}

export { styles as uiStyles };
export { Stepper, type StepperStep } from './Stepper';
export { LoadingState, Skeleton } from './LoadingState';
export { ErrorState } from './Feedback';
export { ImageMetadataNotice } from './ImageMetadataNotice';
export { Disclosure } from './Disclosure';
export { PageHeader } from './PageHeader';
export { Surface, type SurfaceProps, type SurfaceTone } from './Surface';
export { MetricReadout, StatGroup, StatusIndicator } from './Telemetry';
export {
  ActionMenu,
  ActionMenuItem,
  IconButton,
  Tabs,
  type TabItem,
} from './Interactions';
